import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, MapPin, Phone, Search } from 'lucide-react';
import { api, type Customer, type CustomerScope, type CustomerSearch } from '@/lib/api';
import { cn } from '@/lib/utils';

/** Délai avant de lancer la recherche pendant la frappe. */
function useDebounced<T>(value: T, ms = 250): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/** Clients par page. */
const PAGE = 20;

const SCOPES: { value: CustomerScope; label: string }[] = [
  { value: 'mine', label: 'Mes clients' },
  { value: 'all', label: 'Tous les clients' },
];

/**
 * Choix du client chez qui le rep se trouve. Par défaut, ses clients (ses n°
 * vendeur ERP) ; « Tous les clients » élargit la recherche.
 */
export default function CustomerPicker({
  onPick,
  defaultScope,
}: {
  onPick: (c: Customer) => void;
  defaultScope: CustomerScope;
}) {
  const [q, setQ] = useState('');
  const [scope, setScope] = useState(defaultScope);
  const [page, setPage] = useState(0);
  const term = useDebounced(q.trim());
  // Nouvelle recherche ou autre portée : retour à la première page.
  useEffect(() => setPage(0), [term, scope]);

  const search = useQuery({
    queryKey: ['customers', scope, term, page],
    queryFn: () => {
      const params = new URLSearchParams({ limit: String(PAGE), offset: String(page * PAGE), scope });
      if (term) params.set('q', term);
      return api<CustomerSearch>(`/api/customers?${params}`);
    },
    placeholderData: (prev) => prev,
  });
  const data = search.data;
  const total = data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE));
  const noNumbers = data?.scope === 'mine' && data.myNumbers.length === 0;

  // Changer de page ramène le haut de la liste à l'écran.
  const top = useRef<HTMLElement>(null);
  function goTo(p: number) {
    setPage(p);
    top.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  return (
    <section ref={top} className="panel scroll-mt-20 overflow-hidden">
      <div className="flex flex-wrap items-center gap-3 border-b p-3 sm:p-4">
        <div className="bg-muted inline-flex shrink-0 rounded-lg p-1" role="radiogroup" aria-label="Clients affichés">
          {SCOPES.map((s) => (
            <button
              key={s.value}
              role="radio"
              aria-checked={scope === s.value}
              onClick={() => setScope(s.value)}
              className={cn(
                'h-10 rounded-md px-4 text-sm font-medium transition-colors',
                scope === s.value ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {s.label}
            </button>
          ))}
        </div>
        <div className="relative min-w-60 flex-1">
          <Search className="text-muted-foreground absolute top-1/2 left-3 size-5 -translate-y-1/2" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Nom, numéro, ville ou téléphone"
            aria-label="Chercher un client"
            inputMode="search"
            autoCorrect="off"
            spellCheck={false}
            className="border-input bg-card focus:border-primary focus:ring-primary/15 h-12 w-full rounded-lg border pr-3 pl-10 text-base focus:ring-4 focus:outline-none"
          />
        </div>
        <p className="text-muted-foreground w-full text-sm tabular-nums">
          {data ? (
            <>
              <span className="text-foreground font-bold">{total.toLocaleString('fr-CA')}</span>{' '}
              {total > 1 ? 'clients' : 'client'}
              {total > PAGE &&
                ` · ${(page * PAGE + 1).toLocaleString('fr-CA')} à ${Math.min(total, (page + 1) * PAGE).toLocaleString('fr-CA')}`}
            </>
          ) : (
            'Recherche…'
          )}
        </p>
      </div>

      {search.isError && (
        <p className="text-destructive px-4 pt-3 text-sm">{(search.error as Error).message}</p>
      )}
      {noNumbers && (
        <p className="mx-4 mt-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900">
          Aucun n° vendeur n'est associé à votre compte : demandez-le à un administrateur, ou cherchez dans
          « Tous les clients ».
        </p>
      )}
      {data && total === 0 && !noNumbers && (
        <p className="text-muted-foreground px-4 py-6 text-center text-sm">
          Aucun client ne correspond à « {term} ».
        </p>
      )}

      {/* Rangées denses en deux colonnes sur grand écran : le plus de clients possible à l'écran. */}
      <ul className={cn('grid md:grid-cols-2 md:divide-x', search.isPlaceholderData && 'opacity-60')}>
        {data?.customers.map((c) => (
          <li key={c.id} className="border-b">
            <button
              onClick={() => onPick(c)}
              className={cn(
                'group hover:bg-accent flex min-h-16 w-full items-center gap-3 px-4 py-2.5 text-left transition-colors',
                c.suspended && 'text-muted-foreground',
              )}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-2">
                  <span className={cn('truncate font-bold', !c.suspended && 'text-foreground')} title={c.name}>
                    {c.name}
                  </span>
                  <span className="text-muted-foreground shrink-0 text-sm tabular-nums">#{c.custNum}</span>
                  {c.suspended && (
                    <span className="bg-red-50 text-destructive shrink-0 rounded px-1.5 py-px text-xs font-medium">
                      Compte suspendu
                    </span>
                  )}
                </div>
                <div className="text-muted-foreground flex flex-wrap gap-x-3 text-sm">
                  {c.city && (
                    <span className="flex items-center gap-1">
                      <MapPin className="size-3.5" />
                      {c.city}
                    </span>
                  )}
                  {c.phone && (
                    <span className="flex items-center gap-1 tabular-nums">
                      <Phone className="size-3.5" />
                      {c.phone}
                    </span>
                  )}
                  {/* « Mes clients » : le vendeur est le rep lui-même, inutile de le répéter. */}
                  {scope === 'all' && c.salespersonName && <span>Vendeur : {c.salespersonName}</span>}
                </div>
              </div>
              <ChevronRight className="text-muted-foreground group-hover:text-primary size-5 shrink-0 transition-colors" />
            </button>
          </li>
        ))}
      </ul>

      {pages > 1 && (
        <div className="p-3 sm:p-4">
          <Pager page={page} pages={pages} onGo={goTo} />
        </div>
      )}
    </section>
  );
}

/** ‹ 1 … 4 5 6 … 8 › — boutons assez grands pour le doigt. */
function Pager({ page, pages, onGo }: { page: number; pages: number; onGo: (p: number) => void }) {
  // Toujours la première, la dernière et les voisines de la page courante.
  const shown = [...new Set([0, page - 1, page, page + 1, pages - 1])]
    .filter((p) => p >= 0 && p < pages)
    .sort((a, b) => a - b);
  const btn = 'flex h-10 min-w-10 items-center justify-center rounded-lg border px-2 text-sm font-medium tabular-nums transition-colors';

  return (
    <nav className="flex flex-wrap items-center justify-center gap-1.5" aria-label="Pages de clients">
      <button onClick={() => onGo(page - 1)} disabled={page === 0} className={cn(btn, 'bg-card hover:bg-accent gap-1 px-3 disabled:opacity-40')}>
        <ChevronLeft className="size-4" />
        <span className="hidden sm:inline">Précédent</span>
      </button>
      {shown.map((p, i) => (
        <span key={p} className="flex items-center gap-1.5">
          {i > 0 && p - shown[i - 1]! > 1 && <span className="text-muted-foreground px-1">…</span>}
          <button
            onClick={() => onGo(p)}
            aria-current={p === page ? 'page' : undefined}
            className={cn(btn, p === page ? 'border-primary bg-primary text-primary-foreground' : 'bg-card hover:bg-accent')}
          >
            {p + 1}
          </button>
        </span>
      ))}
      <button
        onClick={() => onGo(page + 1)}
        disabled={page >= pages - 1}
        className={cn(btn, 'bg-card hover:bg-accent gap-1 px-3 disabled:opacity-40')}
      >
        <span className="hidden sm:inline">Suivant</span>
        <ChevronRight className="size-4" />
      </button>
    </nav>
  );
}
